import { DEFAULT_LOCALE } from '@constants/locales';
import { QUERY_KEY } from '@constants/queries';
import { Box, Flex, Grid, Title } from '@mantine/core';
import { TitleUnderline } from '@atoms/TitleUnderline';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { validateZodSchema } from '@utils/zodSchemas';
import { newsDetailsPageZodSchema } from '@utils/zodSchemas/details';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import {
  fetchInfinitePodcasts,
  useFetchInfinitePodcasts,
} from '@hooks/queries/useFetchInfinitePodcasts';
import { PodcastGrid } from '@organisms/frontoffice/PodcastGrid';
import { PodcastCard } from '@molecules/frontoffice/PodcastCard';
import { PublicPodcast } from '@utils/typesPages/types';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';
import { EmptyStateComponent } from '@molecules/frontoffice/EmptyStateComponent';
import { SEO } from '@organisms/SEO';
import { fetchCategories } from '@hooks/queries/useFetchCategories';
import { fetchPublicConfiguration } from '@hooks/queries/useFetchPublicConfiguration';
import { fetchSEO, useFetchSEO } from '@hooks/queries/useFetchSEO';
import {
  fetchPublicity,
  useFetchPublicity,
} from '@hooks/queries/useFetchPublicity';
import { RandomPublicityComponent } from '@organisms/RandomPublicityComponent';

export const getServerSideProps: GetServerSideProps = async ({ ...ctx }) => {
  const parsedQuery = validateZodSchema(newsDetailsPageZodSchema, ctx.params);
  const { data: parsedData } = parsedQuery;

  const queryClient = new QueryClient();

  const paramsPublicity = { fetchAll: true };

  const publicityPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLICITY, paramsPublicity],
    queryFn: async () => {
      return await fetchPublicity(paramsPublicity);
    },
  });

  const categoriesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORIES, { fetchAll: true }],
    queryFn: async () => {
      return await fetchCategories({ fetchAll: true });
    },
  });

  const configurationsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_CONFIGURATION],
    queryFn: async () => {
      return await fetchPublicConfiguration();
    },
  });

  const podcastsPromise = queryClient.prefetchInfiniteQuery({
    queryKey: [QUERY_KEY.FETCH_PODCASTS],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchInfinitePodcasts({ pageParam }),
  });

  const seoPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_SEO],
    queryFn: async () => {
      return await fetchSEO();
    },
  });

  await Promise.all([
    seoPromise,
    podcastsPromise,
    configurationsPromise,
    publicityPromise,
    categoriesPromise,
  ]);

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, [
        'common',
        'podcast',
      ])),
      dehydratedState: dehydrate(queryClient),
      slug: parsedData?.slug ?? '',
    },
  };
};

const PodcastsPage: FC = () => {
  const { t } = useTranslation('podcast');
  const [formattedData, setFormattedData] = useState<PublicPodcast[]>([]);

  const {
    data: infinitePodcasts,
    hasNextPage,
    fetchNextPage,
    isLoading: isLoadingPodcasts,
    isFetched,
  } = useFetchInfinitePodcasts();

  const { data: dataSEO, isLoading: isLoadingSEO } = useFetchSEO();

  const { data: publicity } = useFetchPublicity({ fetchAll: true });

  const SEOPodcasts = dataSEO?.value.find((page) => page.name === 'podcasts');

  useEffect(() => {
    if (infinitePodcasts && infinitePodcasts.pages) {
      const flat = infinitePodcasts.pages.flatMap((podcasts) => podcasts.data);
      setFormattedData(
        flat.filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i)
      );
    }
  }, [infinitePodcasts]);

  const hasSettled = isFetched && !isLoadingPodcasts && !isLoadingSEO;

  if (!hasSettled) {
    return <LoadingStateComponent />;
  }

  if (
    (hasSettled && !infinitePodcasts) ||
    (hasSettled && infinitePodcasts?.pages[0].meta.total_pages === 0)
  ) {
    return <EmptyStateComponent />;
  }

  const highlightedPodcasts = formattedData.filter((p) => p.is_highlighted);
  const remainingPodcasts = formattedData.filter((p) => !p.is_highlighted);

  const hasBannerPublicity =
    publicity?.data && publicity.data.some((pub) => pub.type === 'BANNER');

  return (
    <>
      <SEO
        title={SEOPodcasts?.title}
        keywords={SEOPodcasts?.keywords}
        description={SEOPodcasts?.description}
      />
      {hasBannerPublicity && (
        <Box
          display="flex"
          mb={40}
          sx={(theme) => ({
            width: '100vw',
            marginLeft: 'calc(50% - 50vw)',
            [`@media (min-width: ${theme.breakpoints.md})`]: {
              width: '100%',
              marginLeft: 0,
            },
          })}
        >
          <RandomPublicityComponent
            index={0}
            type="BANNER"
            publicities={publicity?.data ?? []}
          />
        </Box>
      )}
      {highlightedPodcasts.length > 0 && (
        <>
          <Flex direction="column" mb={20} mt={40}>
            <Title
              variant="h2Orange"
              fw={800}
              style={{ textTransform: 'uppercase' }}
            >
              {t('podcasts.highlighted')}
            </Title>
            <TitleUnderline />
          </Flex>
          <Grid columns={3} gutter="md" mt={20} mb={40}>
            {highlightedPodcasts.map((podcast) => (
              <Grid.Col key={podcast.id} span={3} md={1}>
                <PodcastCard podcast={podcast} />
              </Grid.Col>
            ))}
          </Grid>
        </>
      )}
      {remainingPodcasts.length > 0 && (
        <>
          <Flex direction="column" mb={20} mt={40}>
            <Title
              variant="h2Orange"
              fw={800}
              style={{ textTransform: 'uppercase' }}
            >
              {t('podcasts')}
            </Title>
            <TitleUnderline />
          </Flex>
          <Box mt={20}>
            <PodcastGrid
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              podcasts={remainingPodcasts}
              publicities={publicity?.data ?? []}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default PodcastsPage;
